import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  useProductosMaterialContext,
  guardarFacturaBackend,
  enviarPayloadFacturaElectronica,
  emitirEcfDgii,
  revertirFacturaRechazada,
} from 'context';
import { FP, fpInputSx } from 'components/financepro';
import VistaFactura from '../facturapos/VistaFactura';
import tiposDocumentosJson from 'utils/tiposDocumentos.json';
import { hoyLocal } from 'lib/fechaLocal';

const TIPO_FACTURA_CREDITO = 'FACTURA CREDITO';
const TIPO_DOCUMENTO_MOTOR_VENTAS_CREDITO = 'Ventas a Credito';
const DOCUMENTO_DIARIO_VENTAS_CREDITO = 'FACTURA CREDITO';

const moduloContabilidadInstalado = (modulos) => {
  if (modulos == null || modulos === false || modulos === 'false') return false;
  const partes = Array.isArray(modulos) ? modulos : String(modulos).split(/[,;]/);
  return partes.some((m) => String(m || '').trim().toLowerCase() === 'contabilidad');
};

const enviarMotorContableActivo = (valor) => String(valor || '').trim().toUpperCase() === 'S';

const debeEnviarAlMotor = (modulos, enviarMotorContable) =>
  moduloContabilidadInstalado(modulos) && enviarMotorContableActivo(enviarMotorContable);

const bloqueMotorDeTipo = (tipooperacion) => {
  const clave = String(tipooperacion || '').toLowerCase().replace(/\s/g, '');
  if (!clave) return null;
  return tiposDocumentosJson.find((e) => e?.[clave])?.[clave] || null;
};

const origenFormaPagoReglas = (tipooperacion) => {
  const bloque = bloqueMotorDeTipo(tipooperacion);
  if (!bloque) return [];
  const raw = bloque.origenformapago ?? bloque.origenformadepago ?? [];
  return (Array.isArray(raw) ? raw : [raw]).map((x) => String(x || '').trim()).filter(Boolean);
};

const tipoTieneOrigenFormaPago = (tipooperacion) => origenFormaPagoReglas(tipooperacion).length > 0;

const payloadFormaPagoSiAplica = (tipoDocumento, documentoFormaPago) => {
  const tipo = String(tipoDocumento || '').trim();
  const documento = String(documentoFormaPago || '').trim();
  if (!tipo || !tipoTieneOrigenFormaPago(tipo) || !documento) return {};
  return { forma_pago: documento };
};

const codigoUsaOrigenFormaPago = (tipooperacion, codigoRegla) => {
  const codigo = String(codigoRegla || '').trim().toLowerCase();
  if (!codigo) return false;
  return origenFormaPagoReglas(tipooperacion).some((c) => c.toLowerCase() === codigo);
};

const normalizarTipoMotor = (valor) =>
  String(valor || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

const documentoMotorDefinido = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  if (!tipo) return false;
  const tipoNorm = normalizarTipoMotor(tipo);
  return (registros || []).some((r) => {
    if (normalizarTipoMotor(r?.tipo_documento) !== tipoNorm) return false;
    if (codigoUsaOrigenFormaPago(tipo, r?.codigo_regla)) return true;
    const aplica = String(r?.aplica_a || 'cuenta').trim().toLowerCase();
    if (aplica === 'json') return true;
    return Boolean(String(r?.cuentacontable || '').trim());
  });
};

const reglasMotorDeDocumento = (registros, tipoDocumento) => {
  const tipoNorm = normalizarTipoMotor(tipoDocumento);
  return (registros || []).filter((r) => normalizarTipoMotor(r?.tipo_documento) === tipoNorm);
};

const CLAVES_META_MOTOR = new Set([
  'tipodocumento',
  'nfactura',
  'documento',
  'numero',
  'suplidor',
  'fecha',
  'tasa',
  'usuario',
  'concepto',
  'valor',
  'valor_total',
  'lineas',
  'cuentas',
]);

const redondearDinero2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const construirDocumentoMotorVenta = (tipoDoc, reglas, ctx) => {
  const lineasFp = Array.isArray(ctx.lineas)
    ? ctx.lineas.filter((ln) => ln?.cuenta && (ln.monto || 0) > 0)
    : [];
  const valor = redondearDinero2(ctx.valorTotal);
  const montos = ctx.montos || {};
  const doc = {
    tipodocumento: tipoDoc,
    nfactura: ctx.nfactura,
    documento: ctx.documento || DOCUMENTO_DIARIO_VENTAS_CREDITO,
    fecha: ctx.fecha,
    tasa: ctx.tasa,
    usuario: ctx.usuario,
    concepto: ctx.concepto,
    valor,
    lineas: lineasFp,
    permitir_descuadre: true,
  };
  if (ctx.numero != null && ctx.numero !== '') {
    doc.numero = ctx.numero;
  }
  (reglas || []).forEach((regla) => {
    const codigo = String(regla?.codigo_regla || '').trim();
    if (!codigo || CLAVES_META_MOTOR.has(codigo.toLowerCase())) return;
    const usaFp =
      codigoUsaOrigenFormaPago(tipoDoc, codigo) ||
      String(regla?.aplica_a || '').trim().toLowerCase() === 'json';
    if (usaFp) {
      doc[codigo] = lineasFp;
      return;
    }
    const extra = montos[codigo] ?? montos[codigo.toLowerCase()];
    doc[codigo] = redondearDinero2(extra != null ? extra : valor);
  });
  return doc;
};

const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || '').replace(/\D/g, '');
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || '').split(':')[1]?.replace(/\D/g, '') || '';
};

/** Códigos e-CF permitidos en POS (sufijo del nombre, p.ej. "CONSUMO:E32" → 32). */
const CODIGOS_DOCUMENTO_POS = ['31', '32', '44', '45', '99'];

const LIMITE_CONSUMO_SIN_RNC = 250000;

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatearFecha = (valor) => {
  if (!valor) return '—';
  const s = String(valor);
  const match = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return s;
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

const mapearItemConduce = (it, productos, esEspecial) => {
  const codigo = it.item || it.codigo || '';
  const prod = (productos || []).find((p) => String(p.codigo) === String(codigo));
  const cantidad = parseFloat(it.cantidad) || 0;
  const precio = parseFloat(it.precio) || parseFloat(prod?.precio) || 0;
  const subtotal = Math.max(0, cantidad * precio);
  const subtotalOrig = parseFloat(it.subtotal) || subtotal;
  const itbisGuardado = parseFloat(it.itbis) || 0;
  let impuesto = parseFloat(prod?.impuesto ?? prod?.itbis) || 0;
  if (!impuesto && subtotalOrig > 0 && itbisGuardado > 0) {
    impuesto = (itbisGuardado / subtotalOrig) * 100;
  }
  return {
    id: it.id || `${codigo}-${Math.random()}`,
    codigo,
    descripcion: it.descripcion || prod?.descripcion || '',
    tipo: 'producto',
    cantidad,
    precio,
    descuento: 0,
    descuento_general: 0,
    subtotal,
    existencia: parseFloat(prod?.existencia) || 0,
    departamento: prod?.departamento || null,
    impuesto: esEspecial ? 0 : impuesto,
    nombreimpuesto: esEspecial ? null : prod?.nombreimpuesto || null,
    tipoproducto: 'item',
    localidad: prod?.localidad || null,
    unidad: it.unidad || 'UND',
  };
};

/**
 * Diálogo para convertir un conduce Recibido a factura a crédito.
 * Permite editar precios y cantidades, recalcula ITBIS y guarda con las
 * mismas reglas de FacturaCredito (e-CF incluido), sin mover inventario ni transdet.
 */
const ConvertirConduceAFactura = ({
  open,
  onClose,
  onExito,
  conduces = [],
  puedeModificar = true,
}) => {
  const { controller, selectedCompany, showSuccess, showError, showWarning } =
    useProductosMaterialContext();
  const {
    nombreNegocio,
    usuarioLog,
    claveLog,
    comprobanteelectronico,
    rnc_empresa,
    direccion_empresa,
    modulos,
    enviarMotorContable,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const [indice, setIndice] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tiposComprobante, setTiposComprobante] = useState([]);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [itemsFactura, setItemsFactura] = useState([]);
  const [impuestosFactura, setImpuestosFactura] = useState([]);
  const [configCliente, setConfigCliente] = useState(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [clienteExiste, setClienteExiste] = useState(false);
  const [rnc, setRnc] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [opcionesRnc, setOpcionesRnc] = useState([]);
  const [cargandoRnc, setCargandoRnc] = useState(false);
  const busquedaRncTimerRef = useRef(null);
  const [observaciones, setObservaciones] = useState('');
  const [vistaFacturaAbierta, setVistaFacturaAbierta] = useState(false);
  const [datosVistaFactura, setDatosVistaFactura] = useState({});
  const [colaRestante, setColaRestante] = useState(null);

  const conduceActual = conduces[indice] || null;

  const docFiscalSeleccionado = useMemo(
    () => tiposComprobante.find((t) => t.nombre === tipoDocumento) || null,
    [tiposComprobante, tipoDocumento]
  );

  const esE32 = useMemo(() => {
    if (!docFiscalSeleccionado) return false;
    const encabezado = (docFiscalSeleccionado.nombre || '').split(':')[1]?.trim().toUpperCase() || '';
    return encabezado.startsWith('E32') || String(docFiscalSeleccionado.codigo) === '32';
  }, [docFiscalSeleccionado]);

  const esEspecial = useMemo(() => {
    if (!docFiscalSeleccionado) {
      return String(tipoDocumento || '').toLowerCase().includes('especial');
    }
    const codigo = codigoTipoDocumento(docFiscalSeleccionado);
    const encabezado = (docFiscalSeleccionado.nombre || '').split(':')[1]?.trim().toUpperCase() || '';
    return (
      codigo === '44' ||
      encabezado.startsWith('E44') ||
      String(tipoDocumento || '').toLowerCase().includes('especial')
    );
  }, [docFiscalSeleccionado, tipoDocumento]);

  const esEspecialRef = useRef(esEspecial);
  esEspecialRef.current = esEspecial;

  const subtotalAntesDescuentos = itemsFactura.reduce(
    (sum, item) => sum + (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio) || 0),
    0
  );
  const subtotal = itemsFactura.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);
  const totalImpuestos = esEspecial
    ? 0
    : impuestosFactura.reduce((sum, imp) => sum + (parseFloat(imp.monto) || 0), 0);
  const total = subtotal + totalImpuestos;

  const e32RequiereRnc = esE32 && total >= LIMITE_CONSUMO_SIN_RNC;
  const rncComprador = esE32 && !e32RequiereRnc ? '' : rnc.trim();
  const razonSocialComprador = esE32 && !e32RequiereRnc ? '' : razonSocial.trim();

  const itbisLinea = (item) => {
    if (esEspecial) return 0;
    const sub = parseFloat(item.subtotal) || 0;
    const tasa = parseFloat(item.impuesto) || 0;
    return sub * (tasa / 100);
  };

  const cargarProductos = async () => {
    const response = await fetch(servidor + '/api/facturabackend_obtenerproductos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        anomesfacturacion: 'productosnoserializados',
        lista_precio_id: null,
        localidad: localStorage.getItem('sucursal') || '',
      }),
    });
    if (!response.ok) return [];
    const data = await response.json();
    const prods = data.productos || [];
    setProductos(prods);
    return prods;
  };

  const cargarClientes = async () => {
    const response = await fetch(servidor + '/api/clientesbackend_buscar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        tipo: 'Cliente',
        descripcion: '',
      }),
    });
    if (!response.ok) return [];
    const data = await response.json();
    const lista = data.resultados || data.clientes || [];
    setClientes(lista);
    return lista;
  };

  const cargarTiposComprobante = async () => {
    const response = await fetch(servidor + '/api/facturabackend_listartiposdocumentos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
      }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.Error || 'No se pudieron cargar los tipos de comprobante');
    }
    const data = await response.json();
    const activos = (data.tipos || []).filter(
      (tipo) =>
        tipo.activo === 'S' &&
        CODIGOS_DOCUMENTO_POS.includes(codigoTipoDocumento(tipo))
    );
    setTiposComprobante(activos);
    return activos;
  };

  const buscarRncs = async (busqueda = '') => {
    if (!usuarioLog) return;
    setCargandoRnc(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_buscarrnc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          busqueda: (busqueda || '').trim(),
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setOpcionesRnc(data.rncs || []);
      }
    } catch {
      // El campo sigue usable como texto libre.
    } finally {
      setCargandoRnc(false);
    }
  };

  const cargarConfigCliente = async (clienteId) => {
    if (!clienteId) {
      setConfigCliente(null);
      return null;
    }
    try {
      const response = await fetch(servidor + '/api/clientesbackend_obtenerporid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          id: clienteId,
        }),
      });
      if (!response.ok) {
        setConfigCliente(null);
        return null;
      }
      const data = await response.json();
      const cli = data.cliente || null;
      setConfigCliente(cli);
      return cli;
    } catch {
      setConfigCliente(null);
      return null;
    }
  };

  const aplicarConduce = useCallback(
    async (conduce, prods, listaClientes, especial) => {
      if (!conduce) return;
      const items = (conduce.items || []).map((it) => mapearItemConduce(it, prods, especial));
      setItemsFactura(items);
      setObservaciones(
        `Generada desde conduce #${conduce.id}. ${conduce.observaciones || ''}`.trim()
      );
      const clienteId = conduce.cliente_id;
      let cliente =
        (listaClientes || []).find((c) => String(c.id) === String(clienteId)) || null;
      if (!cliente && conduce.cliente) {
        cliente =
          (listaClientes || []).find(
            (c) =>
              String(c.descrip || c.descripcion || '') === String(conduce.cliente) ||
              String(c.id) === String(conduce.cliente)
          ) || null;
      }
      const config = await cargarConfigCliente(cliente?.id || clienteId);
      const existe = Boolean(config);
      setClienteExiste(existe);
      if (!cliente && config) {
        cliente = {
          id: config.id || clienteId,
          descrip: config.descrip || config.descripcion || conduce.cliente || '',
          rnc: config.rnc || '',
        };
      }
      setClienteSeleccionado(existe ? cliente || { id: clienteId, descrip: conduce.cliente || '' } : null);
      const rncCli = String(config?.rnc || cliente?.rnc || '').trim();
      const nombreCli = String(
        config?.descrip ||
          config?.descripcion ||
          cliente?.descrip ||
          cliente?.descripcion ||
          conduce.cliente ||
          conduce.cliente_nombre ||
          ''
      ).trim();
      setRnc(rncCli);
      setRazonSocial(nombreCli);
    },
    [selectedCompany, servidor, usuarioLog, claveLog]
  );

  useEffect(() => {
    if (!open) {
      setIndice(0);
      setTipoDocumento('');
      setItemsFactura([]);
      setImpuestosFactura([]);
      setClienteSeleccionado(null);
      setConfigCliente(null);
      setClienteExiste(false);
      setRnc('');
      setRazonSocial('');
      setOpcionesRnc([]);
      setObservaciones('');
      setColaRestante(null);
      return;
    }
    let cancelado = false;
    const iniciar = async () => {
      setCargando(true);
      try {
        const [prods, listaClientes, tipos] = await Promise.all([
          cargarProductos(),
          cargarClientes(),
          cargarTiposComprobante(),
        ]);
        if (cancelado) return;
        if (tipos.length > 0) {
          setTipoDocumento((prev) => prev || tipos[1]?.nombre || tipos[0].nombre);
        }
        await aplicarConduce(conduces[0], prods, listaClientes, false);
      } catch (e) {
        if (!cancelado) {
          showError(e?.message || 'Error al cargar datos para convertir');
          onClose?.();
        }
      } finally {
        if (!cancelado) setCargando(false);
      }
    };
    iniciar();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || indice === 0) return;
    aplicarConduce(conduces[indice], productos, clientes, esEspecial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indice]);

  useEffect(() => {
    if (open && (!esE32 || e32RequiereRnc)) {
      buscarRncs('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, esE32, e32RequiereRnc]);

  const calcularImpuestos = async () => {
    if (esEspecialRef.current || subtotal <= 0 || itemsFactura.length === 0) {
      setImpuestosFactura([]);
      return;
    }
    try {
      const response = await fetch(servidor + '/api/facturabackend_calcularimpuestos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          subtotal,
          itemsfactura: itemsFactura,
        }),
      });
      if (esEspecialRef.current) {
        setImpuestosFactura([]);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        if (data.impuestos && Array.isArray(data.impuestos) && data.impuestos.length > 0) {
          setImpuestosFactura(
            data.impuestos.map((imp) => ({
              id: imp.id || imp.codigo || Date.now() + Math.random(),
              codigo: imp.codigo || imp.nombre || 'IMP',
              nombre: imp.nombre || imp.descripcion || 'Impuesto',
              porcentaje: parseFloat(imp.porcentaje) || 0,
              monto: parseFloat(imp.monto) || 0,
            }))
          );
        } else {
          const monto = itemsFactura.reduce((sum, it) => sum + itbisLinea(it), 0);
          setImpuestosFactura(
            monto > 0
              ? [
                  {
                    id: 'itbis',
                    codigo: 'ITBIS',
                    nombre: 'ITBIS',
                    porcentaje: 0,
                    monto,
                  },
                ]
              : []
          );
        }
      }
    } catch {
      const monto = itemsFactura.reduce((sum, it) => {
        const sub = parseFloat(it.subtotal) || 0;
        const tasa = parseFloat(it.impuesto) || 0;
        return sum + (esEspecialRef.current ? 0 : sub * (tasa / 100));
      }, 0);
      setImpuestosFactura(
        monto > 0
          ? [{ id: 'itbis', codigo: 'ITBIS', nombre: 'ITBIS', porcentaje: 0, monto }]
          : []
      );
    }
  };

  useEffect(() => {
    if (esEspecial || subtotal <= 0) {
      setImpuestosFactura([]);
      return;
    }
    calcularImpuestos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, itemsFactura, esEspecial]);

  const actualizarItem = (id, campo, valor) => {
    const num = parseFloat(valor);
    if (isNaN(num) || num < 0) return;
    if (campo === 'cantidad' && num <= 0) return;
    setItemsFactura((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const siguiente = { ...item, [campo]: num };
        const cant = parseFloat(siguiente.cantidad) || 0;
        const precio = parseFloat(siguiente.precio) || 0;
        siguiente.subtotal = Math.max(0, cant * precio);
        return siguiente;
      })
    );
  };

  const validarClienteYLimites = () => {
    if (!clienteExiste || !clienteSeleccionado) {
      showError('El cliente del conduce no existe en el maestro. No se puede emitir la factura.');
      return false;
    }
    if (!esE32 || e32RequiereRnc) {
      const motivo = e32RequiereRnc
        ? 'para facturas de consumo de RD$250,000 o más'
        : 'para este tipo de documento';
      if (!rnc.trim()) {
        showError(`Debe ingresar el RNC / Cédula del cliente ${motivo}`);
        return false;
      }
      if (!razonSocial.trim()) {
        showError(`Debe ingresar la razón social / nombre del cliente ${motivo}`);
        return false;
      }
    }
    if (!configCliente) return true;
    const limCredito = parseFloat(configCliente.limcredito) || 0;
    const limCompra = parseFloat(configCliente.limcompra) || 0;
    if (limCredito > 0 && total > limCredito) {
      showError(
        `El total (${total.toFixed(2)}) supera el límite de crédito del cliente (${limCredito.toFixed(2)})`
      );
      return false;
    }
    if (limCompra > 0 && total > limCompra) {
      showError(
        `El total (${total.toFixed(2)}) supera el límite de compra del cliente (${limCompra.toFixed(2)})`
      );
      return false;
    }
    return true;
  };

  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'factura_ecf.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const descargarXmlECF = (xmlTexto, filename) => {
    descargarArchivoECF(new Blob([xmlTexto], { type: 'text/xml' }), filename);
  };

  const obtenerConfiguracionParaTipo = async (tipoDoc) => {
    if (!selectedCompany?.db_empresa) return null;
    const nombreTipoTrim = (tipoDoc || '').trim();
    const intentos = [];
    if (nombreTipoTrim) intentos.push(nombreTipoTrim);
    const upper = nombreTipoTrim.toUpperCase();
    if (upper && !intentos.includes(upper)) intentos.push(upper);
    for (const fallback of ['FACTURA CREDITO', 'FACTURA CONTADO', 'FACTURA POS', 'Factura']) {
      if (!intentos.includes(fallback)) intentos.push(fallback);
    }
    let ultimaRespuesta = null;
    for (const intento of intentos) {
      try {
        const response = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tipo_documento: intento,
          }),
        });
        if (!response.ok) continue;
        const data = await response.json();
        ultimaRespuesta = data?.configuracion || ultimaRespuesta;
        if (data?.configuracionGuardada && data?.configuracion) {
          return data.configuracion;
        }
      } catch (_) {
        // intento siguiente
      }
    }
    return ultimaRespuesta;
  };

  const enviarAlMotorContableVenta = useCallback(
    async (tipoDoc, ctxDocumento) => {
      if (!debeEnviarAlMotor(modulos, enviarMotorContable)) return;
      if (!selectedCompany?.db_empresa) return;
      try {
        const reglasRes = await fetch(`${servidor}/contabilidadbackend_listarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tipodocumento: tipoDoc,
            ...payloadFormaPagoSiAplica(tipoDoc, ctxDocumento.formaPago),
          }),
        });
        const reglasData = await reglasRes.json().catch(() => ({}));
        if (!reglasRes.ok) {
          showWarning(reglasData.Error || 'No se pudieron consultar las reglas del motor contable');
          return;
        }
        const reglasDoc = reglasMotorDeDocumento(reglasData.registros, tipoDoc);
        if (!documentoMotorDefinido(reglasDoc, tipoDoc)) {
          showWarning(`No hay reglas del motor para ${tipoDoc}; no se registró en diario/histdia`);
          return;
        }
        const payloadDocumento = construirDocumentoMotorVenta(tipoDoc, reglasDoc, ctxDocumento);
        const motorRes = await fetch(`${servidor}/contabilidadbackend_enviarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            modulos,
            documento: payloadDocumento,
          }),
        });
        const motorData = await motorRes.json().catch(() => ({}));
        if (!motorRes.ok) {
          showWarning(motorData.Error || 'No se pudo enviar la venta al motor contable');
          return;
        }
        if (motorData.omitido) {
          if (motorData.mensaje) showWarning(motorData.mensaje);
          return;
        }
        if (motorData.ok) {
          if (motorData.descuadrado) {
            showWarning(motorData.mensaje || 'Motor contable: asiento guardado descuadrado');
          } else {
            showSuccess(motorData.mensaje || 'Motor contable: asiento generado');
          }
        } else if (motorData.mensaje) {
          showWarning(motorData.mensaje);
        }
      } catch {
        showWarning('Error de conexión al enviar la venta al motor contable');
      }
    },
    [modulos, enviarMotorContable, servidor, selectedCompany, usuarioLog, claveLog, showWarning, showSuccess]
  );

  const avanzarOCerrar = (desdeIndice) => {
    const siguiente = desdeIndice + 1;
    if (siguiente < conduces.length) {
      setIndice(siguiente);
      return;
    }
    onExito?.();
    onClose?.();
  };

  const guardarFactura = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para convertir a facturas');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!conduceActual) {
      showError('No hay conduce seleccionado');
      return;
    }
    if (itemsFactura.length === 0) {
      showError('El conduce no tiene ítems');
      return;
    }
    if (!tipoDocumento) {
      showError('Seleccione el tipo de comprobante');
      return;
    }
    if (!validarClienteYLimites()) return;

    const fechaMotor = hoyLocal();
    const enviarMotor = debeEnviarAlMotor(modulos, enviarMotorContable);
    const tipoDocMotor = TIPO_DOCUMENTO_MOTOR_VENTAS_CREDITO;
    const ingresosMotor = redondearDinero2(subtotalAntesDescuentos);
    const itbisMotor = redondearDinero2(esEspecial ? 0 : totalImpuestos);
    const totalMotor = redondearDinero2(total);
    const montosMotor = enviarMotor
      ? {
          ingreso: ingresosMotor,
          ingresos: ingresosMotor,
          itbis: itbisMotor,
          descuentos: 0,
          cuentasxcobrar: totalMotor,
        }
      : null;
    const payloadMotor = enviarMotor
      ? {
          modulos,
          tipodocumento: tipoDocMotor,
          documento_diario: DOCUMENTO_DIARIO_VENTAS_CREDITO,
          lineas_motor: [],
          montos: montosMotor,
        }
      : {};

    setGuardando(true);
    try {
      const itemsConDescuentoGeneral = itemsFactura.map((item) => ({
        ...item,
        descuento_general: 0,
        subtotal: Math.max(0, parseFloat(item.subtotal) || 0),
        impuesto: esEspecial ? 0 : parseFloat(item.impuesto) || 0,
      }));

      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        anomesfacturacion: 'productosnoserializados',
        localidad: localStorage.getItem('sucursal') || '',
        omitir_inventario: 'S',
        ...payloadMotor,
        factura: {
          cliente: String(clienteSeleccionado.id),
          rnc: rncComprador,
          razonsocial:
            razonSocial.trim() ||
            clienteSeleccionado?.descrip ||
            clienteSeleccionado?.descripcion ||
            configCliente?.descrip ||
            '',
          items: itemsConDescuentoGeneral,
          impuestos: esEspecial ? [] : impuestosFactura,
          subtotal,
          total_descuentos: 0,
          total_impuestos: esEspecial ? 0 : totalImpuestos,
          total,
          ncf: '',
          tipo_documento: tipoDocumento,
          tipo_factura: TIPO_FACTURA_CREDITO,
          fecha: fechaMotor,
          usuario: usuarioLog || '',
          observaciones: observaciones.trim(),
          pago: null,
          cotizacion_id: null,
          orden_pedido_id: null,
          comprobanteelectronico: comprobanteelectronico === 'S' ? 'S' : 'N',
          modulosasignados: modulos,
          omitir_inventario: 'S',
          conduce_id: conduceActual.id,
          ...(enviarMotor
            ? {
                tipodocumento: tipoDocMotor,
                documento_diario: DOCUMENTO_DIARIO_VENTAS_CREDITO,
              }
            : {}),
        },
      };

      const { ok, data, error } = await guardarFacturaBackend(servidor, body);
      if (!ok) {
        showError(error || 'Error al guardar la factura');
        return;
      }

      showSuccess(`Factura ${data.documento}-${data.numero} guardada`);

      let fechaFirmaEcf = '';
      let codigoSeguridadEcf = '';
      let montoTotalEcf = null;
      let facturaRevertida = false;

      try {
        if (comprobanteelectronico === 'S') {
          const ecfPayload = {
            comprobanteelectronico,
            rnc: rncComprador,
            razonsocial: razonSocialComprador,
            comprobante: data.comprobantefiscal || '',
            tipocomprobante: tipoDocumento,
            usuario: usuarioLog || '',
            password: claveLog || '',
            pago: null,
            items: itemsConDescuentoGeneral.map((it) => ({
              item: it.descripcion,
              cantidad: parseFloat(it.cantidad) || 0,
              valor: parseFloat(it.precio) || 0,
              descuento:
                (parseFloat(it.descuento) || 0) + (parseFloat(it.descuento_general) || 0),
              tasaitbis: esEspecial ? 0 : parseFloat(it.impuesto) || 0,
            })),
          };
          const ecf = await enviarPayloadFacturaElectronica(servidor, ecfPayload);
          if (ecf.ok) {
            descargarArchivoECF(ecf.blob, ecf.filename);
          } else {
            showError(ecf.error || 'No se pudo generar el comprobante electrónico');
          }

          const emision = await emitirEcfDgii(servidor, {
            ...ecfPayload,
            rncemisor: rnc_empresa || '',
            razonsocialemisor: typeof nombreNegocio === 'string' ? nombreNegocio : '',
            direccionemisor: direccion_empresa || '',
          });
          if (emision.ok) {
            descargarXmlECF(emision.xml, `${emision.encf || 'ecf'}.xml`);
            if (emision.xml_rfce) {
              descargarXmlECF(emision.xml_rfce, `${emision.encf || 'ecf'}-resumen.xml`);
            }
            fechaFirmaEcf = emision.fecha_firma || '';
            codigoSeguridadEcf = emision.codigo_seguridad || '';
            montoTotalEcf = emision.monto_total ?? null;
            const etiquetas = {
              aceptado: 'aceptado por la DGII',
              aceptado_condicional: 'aceptado condicional por la DGII',
              en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ''}`,
              rechazado: 'RECHAZADO por la DGII',
              pendiente_envio: 'firmado; DGII inaccesible, se reenviará',
              error: 'firmado; el envío a la DGII falló',
            };
            const detalleDgii = (emision.respuesta_dgii?.mensajes || [])
              .map((m) => `${m.valor || m.mensaje || ''}${m.codigo ? ` (${m.codigo})` : ''}`)
              .filter((t) => t.trim())
              .join(' · ');
            const mensaje =
              `e-CF ${emision.encf} ${etiquetas[emision.estado] || emision.estado}` +
              ` · código ${emision.codigo_seguridad}` +
              (detalleDgii ? ` — DGII: ${detalleDgii}` : '');
            if (emision.estado === 'rechazado') {
              showError(mensaje);
              const revertBody = {
                id_empresa: selectedCompany,
                usuario: usuarioLog || '',
                password: claveLog || '',
                nfact: data.numero,
                tipo_documento: tipoDocumento,
                anomesfacturacion: 'productosnoserializados',
                motoraguardar: 'N',
                omitir_inventario: 'S',
              };
              const rev = await revertirFacturaRechazada(servidor, revertBody);
              if (rev.ok) {
                showError(
                  `Factura ${data.documento}-${data.numero} revertida automáticamente (e-CF rechazado)`
                );
              } else {
                showError(rev.error || 'No se pudo revertir la factura rechazada');
              }
              facturaRevertida = true;
            } else {
              showSuccess(mensaje);
            }
          } else {
            showError(emision.error || 'No se pudo emitir el e-CF');
          }
        }
      } catch (eEcf) {
        showError(eEcf?.message || 'Error al emitir el comprobante electrónico');
      }

      if (enviarMotor && !facturaRevertida) {
        const ctxMotor = {
          valorTotal: totalMotor,
          formaPago: '',
          lineas: [],
          nfactura: String(data.numero || ''),
          documento: DOCUMENTO_DIARIO_VENTAS_CREDITO,
          fecha: fechaMotor,
          tasa: 1,
          usuario: usuarioLog || '',
          concepto: `${tipoDocMotor} ${DOCUMENTO_DIARIO_VENTAS_CREDITO}-${data.numero}`,
          montos: montosMotor,
        };
        const motorSave = data.motor;
        if (motorSave?.registrado) {
          if (motorSave.descuadrado) {
            showWarning(motorSave.mensaje || 'Motor contable: asiento guardado descuadrado');
          } else {
            showSuccess(motorSave.mensaje || 'Motor contable: asiento registrado en diario e histdia');
          }
        } else if (motorSave?.omitido) {
          if (motorSave.mensaje) showWarning(motorSave.mensaje);
        } else {
          if (motorSave?.mensaje) showWarning(motorSave.mensaje);
          await enviarAlMotorContableVenta(tipoDocMotor, ctxMotor);
        }
      }

      if (facturaRevertida) {
        return;
      }

      const tipoDocumentoResuelto = docFiscalSeleccionado?.nombre || tipoDocumento;
      const configuracionImpresion = await obtenerConfiguracionParaTipo('FACTURA CREDITO');
      setDatosVistaFactura({
        items: itemsConDescuentoGeneral,
        impuestos: esEspecial ? [] : impuestosFactura,
        subtotal,
        totalDescuentos: 0,
        totalImpuestos: esEspecial ? 0 : totalImpuestos,
        total,
        documento: data.documento,
        numero: String(data.numero || ''),
        fecha: fechaMotor,
        ncf: data.comprobantefiscal || '',
        tipoDocumento: tipoDocumentoResuelto,
        observaciones,
        empresa: selectedCompany?.empresa || selectedCompany?.nombre || selectedCompany,
        id_empresa: selectedCompany,
        tipo_factura: TIPO_FACTURA_CREDITO,
        cliente_nombre: razonSocialComprador || conduceActual.cliente || '',
        comprobanteelectronico,
        codigocomprobante: docFiscalSeleccionado?.codigo ?? '',
        fechavencimiento: docFiscalSeleccionado?.fecha_valida ?? '',
        rnc: rncComprador,
        fecha_firma: fechaFirmaEcf,
        codigo_seguridad: codigoSeguridadEcf,
        monto_total_ecf: montoTotalEcf,
        configuracion: configuracionImpresion || undefined,
      });
      setColaRestante(indice);
      setVistaFacturaAbierta(true);
    } catch (e) {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const cerrarVistaFactura = () => {
    setVistaFacturaAbierta(false);
    const desde = colaRestante == null ? indice : colaRestante;
    setColaRestante(null);
    avanzarOCerrar(desde);
  };

  const handleClose = () => {
    if (guardando) return;
    onClose?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            fontWeight: 700,
            color: FP.color.textPrimary,
            fontSize: '0.98rem',
          }}
        >
          Convertir conduce a factura
          {conduces.length > 1 ? ` (${indice + 1} de ${conduces.length})` : ''}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Conduce"
                    value={conduceActual?.id ?? ''}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Fecha"
                    value={formatearFecha(conduceActual?.fecha)}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cliente"
                    value={razonSocial || conduceActual?.cliente || conduceActual?.cliente_nombre || '—'}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText={
                      clienteExiste
                        ? String(configCliente?.rnc || '').trim()
                          ? `RNC en maestro: ${String(configCliente.rnc).trim()}`
                          : 'Cliente encontrado, sin RNC en el maestro'
                        : 'El cliente del conduce no existe en el maestro'
                    }
                    error={!clienteExiste}
                    sx={fpInputSx}
                  />
                </Grid>
                {(!esE32 || e32RequiereRnc) && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        freeSolo
                        options={opcionesRnc}
                        loading={cargandoRnc}
                        disabled={!puedeModificar || guardando}
                        value={rnc}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          const codigo = (option?.rnc || '').trim();
                          const nombre = (option?.descripcion || '').trim();
                          if (codigo && nombre) return `${codigo} — ${nombre}`;
                          return codigo || nombre || '';
                        }}
                        isOptionEqualToValue={(option, value) => {
                          const optRnc = typeof option === 'string' ? option : option?.rnc;
                          const valRnc = typeof value === 'string' ? value : value?.rnc;
                          return String(optRnc || '').trim() === String(valRnc || '').trim();
                        }}
                        filterOptions={(x) => x}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input' || reason === 'clear') {
                            setRnc(value || '');
                            if (busquedaRncTimerRef.current) {
                              clearTimeout(busquedaRncTimerRef.current);
                            }
                            busquedaRncTimerRef.current = setTimeout(() => {
                              buscarRncs(value || '');
                            }, 280);
                          }
                        }}
                        onChange={(_, option) => {
                          if (option && typeof option === 'object') {
                            setRnc((option.rnc || '').trim());
                            if ((option.descripcion || '').trim()) {
                              setRazonSocial(option.descripcion.trim());
                            }
                          } else if (typeof option === 'string') {
                            setRnc(option.trim());
                          } else if (option == null) {
                            setRnc('');
                          }
                        }}
                        renderOption={(props, option) => (
                          <li {...props} key={`${option.rnc}-${option.descripcion}`}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>
                                {option.rnc}
                              </Typography>
                              <Typography sx={{ fontSize: '0.78rem', color: FP.color.textSecondary }}>
                                {option.descripcion || '—'}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            required
                            label="RNC / Cédula"
                            helperText={
                              e32RequiereRnc
                                ? 'Requerido: consumo de RD$250,000 o más (DGII)'
                                : 'Tomado del cliente; busque en el catálogo RNC si falta'
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={fpInputSx}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {cargandoRnc ? <CircularProgress color="inherit" size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Razón social / nombre"
                        value={razonSocial}
                        onChange={(e) => setRazonSocial(e.target.value)}
                        disabled={!puedeModificar || guardando}
                        InputLabelProps={{ shrink: true }}
                        helperText={
                          e32RequiereRnc
                            ? 'Requerido: consumo de RD$250,000 o más (DGII)'
                            : 'Nombre del cliente para el e-CF'
                        }
                        sx={fpInputSx}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Dirección de envío"
                    value={conduceActual?.direccionenvio || '—'}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Conductor"
                    value={conduceActual?.conductor || '—'}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observaciones"
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={fpInputSx} disabled={guardando}>
                    <InputLabel id="tipo-comprobante-conduce-label">Comprobante</InputLabel>
                    <Select
                      labelId="tipo-comprobante-conduce-label"
                      label="Comprobante"
                      value={tipoDocumento}
                      onChange={(e) => setTipoDocumento(e.target.value)}
                    >
                      {tiposComprobante.map((t) => (
                        <MenuItem key={t.nombre || t.id} value={t.nombre}>
                          {t.descripcion ? `${t.nombre} — ${t.descripcion}` : t.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {!tipoDocumento && tiposComprobante.length === 0 && (
                <Alert
                  severity="warning"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${FP.color.warning}`,
                    bgcolor: FP.color.warningSoft,
                  }}
                >
                  No hay tipos de comprobante POS activos (E31, E32, E44, E45 o E99). Configure tipos fiscales en el sistema.
                </Alert>
              )}
              {!cargando && !clienteExiste && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${FP.color.danger}`,
                    bgcolor: FP.color.dangerSoft,
                  }}
                >
                  El cliente de este conduce no existe en el maestro. Debe registrarlo con su RNC para emitir la factura electrónica.
                </Alert>
              )}
              {clienteExiste && (!esE32 || e32RequiereRnc) && !String(configCliente?.rnc || rnc || '').trim() && (
                <Alert
                  severity="warning"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${FP.color.warning}`,
                    bgcolor: FP.color.warningSoft,
                  }}
                >
                  El cliente no tiene RNC en el maestro. Indique el RNC / Cédula para emitir el e-CF.
                </Alert>
              )}

              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', mb: 1, color: FP.color.textPrimary }}>
                Ítems del conduce — puede modificar precio y cantidad
              </Typography>
              <TableContainer
                sx={{
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: 360,
                  mb: 2,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell sx={cellHeadSx}>Código</TableCell>
                      <TableCell sx={cellHeadSx}>Descripción</TableCell>
                      <TableCell align="center" sx={cellHeadSx}>
                        Cantidad
                      </TableCell>
                      <TableCell sx={cellHeadSx}>Unidad</TableCell>
                      <TableCell align="right" sx={cellHeadSx}>
                        Precio
                      </TableCell>
                      <TableCell align="right" sx={cellHeadSx}>
                        Subtotal
                      </TableCell>
                      <TableCell align="right" sx={cellHeadSx}>
                        ITBIS
                      </TableCell>
                    </TableRow>
                    {itemsFactura.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ color: FP.color.textMuted }}>
                          Sin ítems
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemsFactura.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                            {row.codigo || '—'}
                          </TableCell>
                          <TableCell sx={{ color: FP.color.textPrimary, fontSize: '0.85rem' }}>
                            {row.descripcion || '—'}
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              inputProps={{ min: 0.01, step: 0.01, style: { textAlign: 'center' } }}
                              value={row.cantidad}
                              disabled={!puedeModificar || guardando}
                              onChange={(e) => actualizarItem(row.id, 'cantidad', e.target.value)}
                              sx={{ width: 90, ...fpInputSx }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: FP.color.textSecondary }}>
                            {row.unidad || 'UND'}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                              value={row.precio}
                              disabled={!puedeModificar || guardando}
                              onChange={(e) => actualizarItem(row.id, 'precio', e.target.value)}
                              sx={{ width: 120, ...fpInputSx }}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                          >
                            {fmtMoney(row.subtotal)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontVariantNumeric: 'tabular-nums', color: FP.color.textSecondary }}
                          >
                            {fmtMoney(itbisLinea(row))}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: '#FAFBFE',
                  maxWidth: 420,
                  ml: 'auto',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>Subtotal:</Typography>
                  <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    ${fmtMoney(subtotal)}
                  </Typography>
                </Box>
                {!esEspecial && impuestosFactura.length > 0 ? (
                  impuestosFactura.map((imp) => (
                    <Box key={imp.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontWeight: 500, color: FP.color.textSecondary }}>
                        {imp.nombre}
                        {imp.porcentaje ? ` (${parseFloat(imp.porcentaje).toFixed(2)}%)` : ''}:
                      </Typography>
                      <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        ${fmtMoney(imp.monto || 0)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontWeight: 500, color: FP.color.textSecondary }}>ITBIS:</Typography>
                    <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      ${fmtMoney(totalImpuestos)}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    pt: 1,
                    borderTop: `1px solid ${FP.color.border}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>Total:</Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: FP.color.navy,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    ${fmtMoney(total)}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={guardando}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={guardarFactura}
            disabled={
              guardando ||
              cargando ||
              !tipoDocumento ||
              itemsFactura.length === 0 ||
              !puedeModificar ||
              !clienteExiste
            }
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {guardando ? 'Creando factura…' : 'Crear factura'}
          </Button>
        </DialogActions>
      </Dialog>

      <VistaFactura
        open={vistaFacturaAbierta}
        onClose={cerrarVistaFactura}
        facturaData={datosVistaFactura}
      />
    </>
  );
};

export default ConvertirConduceAFactura;
